/* Delaney Atelier — lightweight, dependency-free front-of-house scripts. */
(function () {
  'use strict';

  document.documentElement.classList.remove('no-js');

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----- Soft reveal on scroll (fail-safe: never leaves content blank) ----- */
  function initReveal() {
    var items = Array.prototype.slice.call(document.querySelectorAll('[data-reveal]'));
    if (!items.length) return;

    function revealAll() { items.forEach(function (el) { el.classList.add('is-visible'); }); }

    // No animation path: show everything immediately.
    if (prefersReducedMotion || !('IntersectionObserver' in window)) { revealAll(); return; }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 });

    items.forEach(function (el, i) {
      // Gentle stagger for siblings revealed in the same viewport pass.
      el.style.setProperty('--reveal-delay', (Math.min(i % 6, 4) * 0.08) + 's');
      observer.observe(el);
    });

    // Failsafe: reveal anything still hidden shortly after load, so a missed
    // observer callback, an offscreen render, or a screenshot never leaves a
    // section blank. Content reached before this still animates on scroll.
    function revealRemaining() {
      items.forEach(function (el) {
        if (!el.classList.contains('is-visible')) {
          var r = el.getBoundingClientRect();
          // Reveal instantly (no transition) only for items already scrolled past
          // or far below, so in-view items still get their animation.
          el.classList.add('is-visible');
        }
      });
    }
    if (document.readyState === 'complete') {
      window.setTimeout(revealRemaining, 1600);
    } else {
      window.addEventListener('load', function () { window.setTimeout(revealRemaining, 1600); }, { once: true });
    }
  }

  /* ----- Header shadow on scroll ----- */
  function initHeader() {
    var header = document.querySelector('.site-header');
    if (!header) return;
    var update = function () {
      header.classList.toggle('is-scrolled', window.scrollY > 12);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  /* ----- Mobile navigation (focus-trapped dialog while open) ----- */
  function initNav() {
    var toggle = document.querySelector('[data-nav-toggle]');
    var nav = document.querySelector('[data-nav]');
    var close = document.querySelector('[data-nav-close]');
    var header = document.querySelector('.site-header');
    if (!toggle || !nav) return;

    var background = document.querySelectorAll('main, .site-footer, .announcement-bar');

    function trapTab(e) {
      if (e.key !== 'Tab') return;
      var focusable = nav.querySelectorAll('a[href], button:not([disabled])');
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function setOpen(open) {
      nav.classList.toggle('is-open', open);
      /* The header uses backdrop-filter, which makes it a containing block for
         position:fixed descendants. That would trap and clip the full-screen
         drawer, so drop the filter while the drawer is open. */
      if (header) header.classList.toggle('nav-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.style.overflow = open ? 'hidden' : '';
      background.forEach(function (el) {
        if (open) el.setAttribute('inert', '');
        else el.removeAttribute('inert');
      });
      if (open) {
        nav.setAttribute('role', 'dialog');
        nav.setAttribute('aria-modal', 'true');
        document.addEventListener('keydown', trapTab);
        var firstLink = nav.querySelector('a, button');
        if (firstLink) firstLink.focus();
      } else {
        nav.removeAttribute('role');
        nav.removeAttribute('aria-modal');
        document.removeEventListener('keydown', trapTab);
        toggle.focus();
      }
    }

    toggle.addEventListener('click', function () { setOpen(!nav.classList.contains('is-open')); });
    if (close) close.addEventListener('click', function () { setOpen(false); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) setOpen(false);
    });
    nav.addEventListener('click', function (e) {
      if (e.target.closest('a')) setOpen(false);
    });
  }

  /* ----- Hero video: honor reduced motion, offer pause ----- */
  function initHeroVideo() {
    document.querySelectorAll('[data-hero-video]').forEach(function (hero) {
      var video = hero.querySelector('video');
      var pauseBtn = hero.querySelector('[data-hero-pause]');
      if (!video) return;

      if (prefersReducedMotion) {
        video.removeAttribute('autoplay');
        video.pause();
        if (pauseBtn) {
          pauseBtn.classList.add('is-paused');
          pauseBtn.setAttribute('aria-label', pauseBtn.getAttribute('data-label-play'));
        }
      }

      if (pauseBtn) {
        pauseBtn.addEventListener('click', function () {
          if (video.paused) {
            video.play();
            pauseBtn.classList.remove('is-paused');
            pauseBtn.setAttribute('aria-label', pauseBtn.getAttribute('data-label-pause'));
          } else {
            video.pause();
            pauseBtn.classList.add('is-paused');
            pauseBtn.setAttribute('aria-label', pauseBtn.getAttribute('data-label-play'));
          }
        });
      }
    });
  }

  /* ----- Inquiry prefill: /pages/contact?piece=Name&type=Custom+Commission ----- */
  function initInquiryPrefill() {
    var form = document.querySelector('[data-contact-form]');
    if (!form) return;
    var params = new URLSearchParams(window.location.search);
    var piece = params.get('piece');
    var type = params.get('type');
    var url = params.get('url');

    if (type) {
      var select = form.querySelector('[data-inquiry-type]');
      if (select) {
        var t = type.toLowerCase();
        var chosen = -1;
        for (var i = 0; i < select.options.length; i++) {
          if (select.options[i].value.toLowerCase() === t) { chosen = i; break; }
        }
        if (chosen < 0) {
          var firstWord = t.split(' ')[0];
          for (var k = 0; k < select.options.length; k++) {
            if (select.options[k].value.toLowerCase().indexOf(firstWord) === 0) { chosen = k; break; }
          }
        }
        if (chosen >= 0) select.selectedIndex = chosen;
      }
    }

    var urlField = form.querySelector('[data-product-url]');
    if (url && urlField) urlField.value = url;

    if (!piece) return;

    var regarding = form.querySelector('[data-regarding]');
    if (regarding) {
      regarding.textContent = 'Regarding: ' + piece;
      regarding.classList.add('is-visible');
    }
    var pieceField = form.querySelector('[data-piece-field]');
    if (pieceField && !pieceField.value) pieceField.value = piece;
    var message = form.querySelector('textarea[name="contact[body]"]');
    if (message && !message.value) {
      message.value = 'Hello Kim,\n\nI would love more details about "' + piece + '", including availability, shipping, and anything else I should know.\n\nThank you.';
    }
    var subject = form.querySelector('input[name="contact[subject]"]');
    if (subject) subject.value = 'Inquiry: ' + piece;
  }

  /* ----- Product gallery lightbox ----- */
  function initLightbox() {
    var dialog = document.querySelector('[data-lightbox-dialog]');
    var items = Array.prototype.slice.call(document.querySelectorAll('[data-lightbox-item]'));
    if (!dialog || !items.length || typeof dialog.showModal !== 'function') return;

    var stageImg = dialog.querySelector('[data-lightbox-stage] img');
    var count = dialog.querySelector('[data-lightbox-count]');
    var current = 0;

    function show(index) {
      current = (index + items.length) % items.length;
      stageImg.src = items[current].getAttribute('data-full');
      if (count) count.textContent = (current + 1) + ' / ' + items.length;
    }

    items.forEach(function (item, i) {
      var img = item.querySelector('img');
      if (!img) return;
      img.setAttribute('data-lightbox', '');
      img.setAttribute('tabindex', '0');
      img.setAttribute('role', 'button');
      img.setAttribute('aria-label', 'Enlarge image ' + (i + 1));
      function open() { show(i); dialog.showModal(); }
      img.addEventListener('click', open);
      img.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
      });
    });

    dialog.querySelector('[data-lightbox-close]').addEventListener('click', function () { dialog.close(); });
    dialog.querySelector('[data-lightbox-prev]').addEventListener('click', function () { show(current - 1); });
    dialog.querySelector('[data-lightbox-next]').addEventListener('click', function () { show(current + 1); });
    dialog.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') show(current - 1);
      if (e.key === 'ArrowRight') show(current + 1);
    });
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) dialog.close();
    });
  }

  /* ----- Quantity steppers ----- */
  function initQuantity() {
    document.querySelectorAll('[data-qty]').forEach(function (wrap) {
      var input = wrap.querySelector('input');
      wrap.querySelectorAll('button').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var delta = btn.hasAttribute('data-qty-up') ? 1 : -1;
          var min = parseInt(input.min || '1', 10);
          var next = Math.max(min, (parseInt(input.value, 10) || min) + delta);
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ----- Cart: submit update when quantity changes ----- */
  function initCart() {
    var cartForm = document.querySelector('[data-cart-form]');
    if (!cartForm) return;
    cartForm.addEventListener('change', function (e) {
      if (e.target.matches('[data-cart-qty]')) cartForm.submit();
    });
  }

  /* ----- Ambient editorial video: one autoplay at a time, pause offscreen ----- */
  function initAmbientVideo() {
    var frames = Array.prototype.slice.call(document.querySelectorAll('[data-ambient-video]'));
    if (!frames.length) return;

    var active = null;

    // Apply a slowed, cinematic playback rate (e.g. 0.7) whenever the clip loads or plays.
    frames.forEach(function (frame) {
      var v = frame.querySelector('video');
      if (!v) return;
      var rate = parseFloat(frame.getAttribute('data-playback-rate'));
      if (!(rate > 0)) return;
      var apply = function () { try { v.playbackRate = rate; } catch (e) {} };
      apply();
      v.addEventListener('loadedmetadata', apply);
      v.addEventListener('play', apply);
    });

    function pause(frame) {
      var v = frame.querySelector('video');
      if (v && !v.paused) v.pause();
      var btn = frame.querySelector('[data-ambient-toggle]');
      if (btn) {
        btn.classList.add('is-paused');
        btn.setAttribute('aria-label', btn.getAttribute('data-label-play'));
      }
    }
    function play(frame) {
      if (prefersReducedMotion) return;
      if (active && active !== frame) pause(active);
      var v = frame.querySelector('video');
      if (v) {
        var rate = parseFloat(frame.getAttribute('data-playback-rate') || v.getAttribute('data-playback-rate'));
        if (rate > 0) { try { v.playbackRate = rate; } catch (e) {} }
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      }
      active = frame;
      var btn = frame.querySelector('[data-ambient-toggle]');
      if (btn) {
        btn.classList.remove('is-paused');
        btn.setAttribute('aria-label', btn.getAttribute('data-label-pause'));
      }
    }

    // Pause/play control (works even under reduced motion)
    frames.forEach(function (frame) {
      var btn = frame.querySelector('[data-ambient-toggle]');
      var v = frame.querySelector('video');
      if (!btn || !v) return;
      if (prefersReducedMotion) {
        btn.classList.add('is-paused');
        btn.setAttribute('aria-label', btn.getAttribute('data-label-play'));
      }
      btn.addEventListener('click', function () {
        if (v.paused) play(frame);
        else pause(frame);
      });
    });

    if (prefersReducedMotion || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries) {
      // Prefer the most-visible frame; play it, pause the rest.
      var best = null;
      entries.forEach(function (entry) {
        var frame = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          if (!best || entry.intersectionRatio > best.ratio) best = { frame: frame, ratio: entry.intersectionRatio };
        } else if (entry.intersectionRatio < 0.3) {
          pause(frame);
          if (active === frame) active = null;
        }
      });
      if (best) play(best.frame);
    }, { threshold: [0, 0.3, 0.6, 0.9] });

    frames.forEach(function (frame) { io.observe(frame); });

    window.addEventListener('pagehide', function () { io.disconnect(); }, { once: true });
  }

  /* ----- Film strip: click a poster to play, one at a time, no autoplay ----- */
  function initFilmStrip() {
    document.querySelectorAll('[data-film-strip]').forEach(function (strip) {
      var videos = Array.prototype.slice.call(strip.querySelectorAll('.film-strip__item video'));
      if (!videos.length) return;
      videos.forEach(function (v) {
        v.setAttribute('preload', 'none');
        v.setAttribute('playsinline', '');
        v.setAttribute('tabindex', '0');
        function toggle() {
          if (v.paused) {
            videos.forEach(function (other) { if (other !== v) other.pause(); });
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
          } else {
            v.pause();
          }
        }
        v.addEventListener('click', toggle);
        v.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
        });
      });
    });
  }

  /* ----- Address form: restore the saved country selection ----- */
  function initAddressCountry() {
    document.querySelectorAll('select[data-default]').forEach(function (select) {
      var saved = select.getAttribute('data-default');
      if (!saved) return;
      for (var i = 0; i < select.options.length; i++) {
        if (select.options[i].value === saved || select.options[i].text === saved) {
          select.selectedIndex = i;
          break;
        }
      }
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initReveal();
    initHeader();
    initNav();
    initHeroVideo();
    initInquiryPrefill();
    initLightbox();
    initQuantity();
    initCart();
    initAddressCountry();
    initAmbientVideo();
    initFilmStrip();
  });
})();
